import { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { connect } from "react-redux";
import { BiLoaderAlt } from "react-icons/bi";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  campaignDetails,
  updateCampaign,
} from "../../../../stores/actions/campaign.actions.types";
import { emailTemplateList } from "../../../../stores/actions/emailtemplate.actions.types";

import { TextAreaInput, TextInput } from "../../../UI/FormField";
import EmailTemplateSelect from "../../../Common/EmailTemplateSelect";

const UpdateEmailCampaignPage = ({
  emailTemplateList,
  updateCampaign,
  campaignDetails,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const type = query.get("type");

  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [initialData, setInitialData] = useState(null);
  const [fetching, setFetching] = useState(true);

  const validationSchema = Yup.object({
    name: Yup.string().required("Campaign name is required"),
    description: Yup.string(),
    emailTemplateId: Yup.string().required("Email template is required"),
  });

  useEffect(() => {
    if (!id) return;
    setFetching(true);

    campaignDetails({
      id,
      callback: (res) => {
        if (res.success) {
          const c = res.data?.data;
          setInitialData({
            name: c.name || "",
            description: c.description || "",
            emailTemplateId: c.emailTemplateId || "",
            targetedAudience: c.targetedAudience || "",
            status: c.status || "draft",
          });
        }
        setFetching(false);
      },
    });
  }, [id]);

  useEffect(() => {
    emailTemplateList({
      data: { size: 10000 },
      callback: (res) => {
        if (res.success) {
          setTemplates(res?.data?.emailTemplates || []);
        }
      },
    });
  }, []);

  const handleSubmit = (values) => {
    setLoading(true);
    updateCampaign({
      id,
      data: { ...values, type },
      callback: (res) => {
        setLoading(false);
        if (res.success) {
          navigate(-1);
        }
      },
    });
  };

  return (
    <div className='rounded-[10px] border border-stroke bg-white p-4 shadow-sm dark:border-dark-3 dark:bg-boxdark sm:p-7.5'>
      <div className='max-w-full'>
        {fetching ? (
          <div className='text-center py-10'>Loading...</div>
        ) : (
          <Formik
            initialValues={initialData}
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue, touched, errors }) => (
              <Form className='mx-auto space-y-5 rounded-md p-4 shadow-sm'>
                {/* NAME */}
                <TextInput
                  label='Campaign Name'
                  name='name'
                  placeholder='Campaign Name'
                  className='block w-full rounded-md border px-3 py-2 dark:bg-black dark:text-white'
                />

                {/* DESCRIPTION */}
                <TextAreaInput
                  name='description'
                  label='Description'
                  rows={5}
                  placeholder='Description'
                  className='block w-full rounded-md border px-3 py-2 dark:bg-black'
                />

                {/* TEMPLATE SELECT */}
                <EmailTemplateSelect
                  templates={templates}
                  value={values.emailTemplateId}
                  setFieldValue={setFieldValue}
                />

                {/* TARGET AUDIENCE BUTTONS */}
                {type === "cart-funnel-email" || type === "pending-email" ? (
                  ""
                ) : (
                  <div>
                    <label className='block mb-1 font-medium'>
                      Target Audience
                    </label>
                    <div className='flex gap-2 text-sm'>
                      {["customers", "subscribers", "outreach", "all"].map(
                        (audience) => {
                          const selected = values.targetedAudience === audience;
                          return (
                            <button
                              type='button'
                              key={audience}
                              onClick={() =>
                                setFieldValue("targetedAudience", audience)
                              }
                              className={`px-4 py-1 rounded-full ${
                                selected
                                  ? "bg-red-500 text-white"
                                  : "bg-gray-200 dark:bg-gray-600 dark:text-white text-gray-700"
                              }`}
                            >
                              {audience.charAt(0).toUpperCase() +
                                audience.slice(1)}
                            </button>
                          );
                        }
                      )}
                    </div>
                    {errors.targetedAudience && touched.targetedAudience && (
                      <div className='text-red-500 text-sm mt-1'>
                        {errors.targetedAudience}
                      </div>
                    )}
                  </div>
                )}

                {/* SUBMIT BUTTON */}
                <button
                  type='submit'
                  disabled={loading}
                  className='w-full bg-red-600 text-white py-2 rounded-md'
                >
                  {loading ? (
                    <BiLoaderAlt className='animate-spin text-2xl m-auto text-white' />
                  ) : (
                    "Update Campaign"
                  )}
                </button>
                <button
                  type='button'
                  onClick={() => navigate(-1)}
                  className='w-full bg-gray-300 dark:bg-gray-700 dark:text-white text-gray-800 py-2 rounded-md'
                >
                  Cancel
                </button>
              </Form>
            )}
          </Formik>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  loading: state?.app?.visible,
});

export default connect(mapStateToProps, {
  updateCampaign,
  emailTemplateList,
  campaignDetails,
})(UpdateEmailCampaignPage);
